import React from 'react'
import { useParams, Link } from 'react-router-dom'
import { CheckCircleIcon, ArrowRightIcon } from '@heroicons/react/solid'

import SEOHead from '../utilities/SEOHead'
import { FAQPageSchema, ServiceSchema, BreadcrumbSchema, SpeakableSchema } from '../utilities/StructuredData'
import MarketingNavBar from '../leevo_ui/NavBar/MarketingNavBar/MarketingNavBar'
import MarketingPageFooter from '../leevo_ui/MarketingPageFooter/MarketingPageFooter'
import SimpleJustifiedCTA from '../leevo_ui/SimpleJustifiedCTA/SimpleJustifiedCTA'
import { getSportContent, getAllSportContent } from '../../data/sportContent'
import NotFoundPage from '../ErrorPages/NotFoundPage'

export default function SportLandingPage() {
  const { sportSlug } = useParams()
  const sport = getSportContent(sportSlug)

  if (!sport) {
    return <NotFoundPage />
  }

  const breadcrumbs = [
    { name: 'Home', path: '/' },
    { name: 'Sports', path: '/sports' },
    { name: sport.name, path: `/sports/${sport.slug}` }
  ]

  const relatedSports = getAllSportContent()
    .filter((s) => s.slug !== sport.slug)
    .slice(0, 4)

  return (
    <>
      <SEOHead
        title={sport.title}
        description={sport.metaDescription}
        path={`/sports/${sport.slug}`}
        keywords={sport.keywords}
      />
      <FAQPageSchema faqs={sport.faqs} />
      <ServiceSchema
        sportName={sport.name}
        sportSlug={sport.slug}
        description={sport.metaDescription}
        features={sport.features.map((f) => f.title)}
      />
      <BreadcrumbSchema items={breadcrumbs} />
      <SpeakableSchema path={`/sports/${sport.slug}`} cssSelectors={['h1', '.hero-description', '.key-features']} />

      <div className="bg-white dark:bg-gray-800">
        <MarketingNavBar />

        <main>
          <section className="relative overflow-hidden bg-gradient-to-br from-purple-700 to-purple-900 dark:from-purple-800 dark:to-purple-950">
            <div className="absolute inset-0 bg-grid-white/[0.05] bg-[size:60px_60px]" />
            <div className="relative px-4 py-20 mx-auto max-w-7xl sm:px-6 lg:px-8 lg:py-28">
              <div className="text-center">
                <h1 className="text-4xl font-extrabold tracking-tight text-white sm:text-5xl lg:text-6xl text-balance">
                  {sport.hero.headline}
                </h1>
                <p className="hero-description max-w-3xl mx-auto mt-6 text-xl text-purple-100 text-balance">{sport.hero.subheadline}</p>
                <div className="flex flex-col items-center justify-center gap-4 mt-10 sm:flex-row">
                  <Link
                    to="/onboarding"
                    className="inline-flex items-center px-8 py-4 text-lg font-semibold text-purple-700 bg-white rounded-lg shadow-lg hover:bg-purple-50 transition-colors"
                  >
                    Start Free Trial
                    <ArrowRightIcon className="w-5 h-5 ml-2" />
                  </Link>
                  <Link
                    to="/pricing"
                    className="inline-flex items-center px-8 py-4 text-lg font-semibold text-white border-2 border-white/30 rounded-lg hover:bg-white/10 transition-colors"
                  >
                    View Pricing
                  </Link>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl text-balance">
                  Everything You Need for {sport.name} Program Management
                </h2>
                <p className="max-w-2xl mx-auto mt-4 text-lg text-gray-600 dark:text-gray-300 text-balance">
                  Leevo gives you the tools to run your {sport.name.toLowerCase()} program efficiently, so you can focus on
                  what matters most.
                </p>
              </div>

              <div className="key-features grid gap-8 mt-16 md:grid-cols-2 lg:grid-cols-3">
                {sport.features.map((feature, index) => (
                  <div
                    key={index}
                    className="relative p-8 bg-white dark:bg-gray-800 rounded-xl shadow-sm hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-center justify-center w-12 h-12 mb-6 bg-purple-100 dark:bg-purple-900 rounded-lg">
                      <CheckCircleIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{feature.title}</h3>
                    <p className="mt-3 text-gray-600 dark:text-gray-300">{feature.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-20 bg-white dark:bg-gray-800">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <div className="grid gap-12 lg:grid-cols-2 lg:gap-20 items-center">
                <div>
                  <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl text-balance">
                    Why {sport.name} Programs Choose Leevo
                  </h2>
                  <p className="mt-4 text-lg text-gray-600 dark:text-gray-300 text-balance">
                    Join the growing number of {sport.name.toLowerCase()} facilities that have simplified their operations
                    with Leevo.
                  </p>
                  <ul className="mt-8 space-y-4">
                    {sport.benefits.map((benefit, index) => (
                      <li key={index} className="flex items-start">
                        <CheckCircleIcon className="flex-shrink-0 w-6 h-6 text-green-500 mt-0.5" />
                        <span className="ml-3 text-gray-700 dark:text-gray-200">{benefit}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="mt-10">
                    <Link
                      to="/onboarding"
                      className="inline-flex items-center px-6 py-3 text-base font-medium text-white bg-purple-600 rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      Get Started Free
                      <ArrowRightIcon className="w-5 h-5 ml-2" />
                    </Link>
                  </div>
                </div>
                <div className="relative">
                  <div className="aspect-video bg-gradient-to-br from-purple-100 to-purple-200 dark:from-purple-900 dark:to-purple-800 rounded-2xl flex items-center justify-center">
                    <div className="text-center p-8">
                      <div className="text-6xl font-bold text-purple-600 dark:text-purple-400">30</div>
                      <div className="text-xl text-purple-700 dark:text-purple-300 mt-2">Day Free Trial</div>
                      <div className="text-gray-600 dark:text-gray-400 mt-4">No credit card required</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section className="py-20 bg-gray-50 dark:bg-gray-900">
            <div className="px-4 mx-auto max-w-4xl sm:px-6 lg:px-8">
              <div className="text-center">
                <h2 className="text-3xl font-bold text-gray-900 dark:text-white sm:text-4xl text-balance">
                  Frequently Asked Questions
                </h2>
                <p className="max-w-2xl mx-auto mt-4 text-lg text-gray-600 dark:text-gray-300 text-balance">
                  Common questions about using Leevo for {sport.name.toLowerCase()} programs
                </p>
              </div>

              <div className="mt-12 space-y-8">
                {sport.faqs.map((faq, index) => (
                  <div key={index} className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{faq.question}</h3>
                    <p className="mt-3 text-gray-600 dark:text-gray-300">{faq.answer}</p>
                  </div>
                ))}
              </div>
            </div>
          </section>

          <section className="py-16 bg-white dark:bg-gray-800">
            <div className="px-4 mx-auto max-w-7xl sm:px-6 lg:px-8">
              <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-8">
                Leevo Also Works Great For
              </h2>
              <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {relatedSports.map((relatedSport) => (
                  <Link
                    key={relatedSport.slug}
                    to={`/sports/${relatedSport.slug}`}
                    className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/30 transition-colors group"
                  >
                    <span className="font-medium text-gray-900 dark:text-white group-hover:text-purple-600 dark:group-hover:text-purple-400">
                      {relatedSport.name}
                    </span>
                    <ArrowRightIcon className="inline w-4 h-4 ml-2 text-gray-400 group-hover:text-purple-600 dark:group-hover:text-purple-400" />
                  </Link>
                ))}
              </div>
              <div className="mt-6 text-center">
                <Link
                  to="/sports"
                  className="text-purple-600 dark:text-purple-400 hover:text-purple-700 dark:hover:text-purple-300 font-medium"
                >
                  View all sports we support →
                </Link>
              </div>
            </div>
          </section>

          <SimpleJustifiedCTA showTrialButton />
        </main>

        <MarketingPageFooter />
      </div>
    </>
  )
}
